import React, { useState, useEffect, useRef } from "react";
import "./index.scss";

const SearchableAutocomplete = ({
  options = [],
  value,
  onChange,
  placeholder,
  error,
}) => {
  const [search, setSearch] = useState(value || "");
  const [showOptions, setShowOptions] = useState(false);
  const [highlightIndex, setHighlightIndex] = useState(-1);
  const containerRef = useRef(null);

  // ✅ Sync search value with prop changes (important on back click)
  useEffect(() => {
    setSearch(value || "");
  }, [value]);

  const filteredOptions = options.filter((opt) =>
    opt.toLowerCase().includes(search.toLowerCase())
  );

  const handleSelect = (option) => {
    onChange(option);
    setSearch(option);
    setShowOptions(false);
    setHighlightIndex(-1);
  };

  const handleKeyDown = (e) => {
    if (e.key === "ArrowDown") {
      e.preventDefault();
      setShowOptions(true);
      setHighlightIndex((prev) =>
        prev < filteredOptions.length - 1 ? prev + 1 : 0
      );
    } else if (e.key === "ArrowUp") {
      e.preventDefault();
      setHighlightIndex((prev) =>
        prev > 0 ? prev - 1 : filteredOptions.length - 1
      );
    } else if (e.key === "Enter") {
      e.preventDefault();
      if (showOptions) {
        if (highlightIndex >= 0) {
          handleSelect(filteredOptions[highlightIndex]);
        } else {
          handleSelect(search.trim());
        }
      }
    } else if (e.key === "Escape") {
      setShowOptions(false);
    }
  };

  const handleClickOutside = (e) => {
    if (containerRef.current && !containerRef.current.contains(e.target)) {
      setShowOptions(false);
    }
  };

  useEffect(() => {
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  return (
    <div
      className="searchable-dropdown"
      ref={containerRef}
      role="combobox"
      aria-haspopup="listbox"
      aria-owns="autocomplete-list"
      aria-expanded={showOptions}
    >
      <label className="searchable-label">Choose or Type :</label>
      <input
        type="text"
        value={search}
        placeholder={placeholder || "Select..."}
        onChange={(e) => {
          setSearch(e.target.value);
          setShowOptions(true);
          setHighlightIndex(-1);
        }}
        onFocus={() => setShowOptions(true)}
        onBlur={() => {
          setTimeout(() => {
            if (search.trim() && !options.includes(search.trim())) {
              onChange(search.trim());
            }
            setShowOptions(false);
          }, 150);
        }}
        onKeyDown={handleKeyDown}
        className={`text-grid-input ${error ? "input-error" : ""}`}
        aria-autocomplete="list"
        aria-controls="autocomplete-list"
        aria-activedescendant={
          highlightIndex >= 0 ? `option-${highlightIndex}` : undefined
        }
      />
      {showOptions && (
        <ul id="autocomplete-list" className="dropdown-options" role="listbox">
          {[...filteredOptions]
            .sort((a, b) => a.localeCompare(b))
            .map((option, i) => (
              <li
                key={i}
                id={`option-${i}`}
                className={i === highlightIndex ? "highlighted" : ""}
                onMouseDown={() => handleSelect(option)}
                role="option"
                aria-selected={i === highlightIndex}
              >
                {option}
              </li>
            ))}
        </ul>
      )}
      {error && <div className="error-text">{error}</div>}
    </div>
  );
};

export default SearchableAutocomplete;
